import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { projectSizeOptions } from '@/lib/leadOptions'
import { useCreateLead } from '@/hooks/useLeads'
import { useUsers } from '@/hooks/useUsers'
import { useAuthStore } from '@/stores/authStore'
import { useUIStore } from '@/stores/uiStore'
import { leadService } from '@/api/leadService'
import {
  SourceChannel,
  ProjectSize,
  NextAction,
  LeadCreate,
} from '@/types/api'
import type { DuplicateCheckResponse } from '@/types/api'

const sourceOptions = [
  { value: SourceChannel.PHONE, label: 'Phone' },
  { value: SourceChannel.WALK_IN, label: 'Walk-in' },
  { value: SourceChannel.WEBSITE, label: 'Website' },
  { value: SourceChannel.REFERRAL, label: 'Referral' },
  { value: SourceChannel.GOOGLE_ADS, label: 'Google Ads' },
  { value: SourceChannel.FACEBOOK, label: 'Facebook' },
  { value: SourceChannel.INSTAGRAM, label: 'Instagram' },
  { value: SourceChannel.YELP, label: 'Yelp' },
  { value: SourceChannel.RECURRING_CLIENT, label: 'Recurring Client' },
  { value: SourceChannel.OTHER, label: 'Other' },
]

const nextActionOptions = [
  { value: NextAction.CALL, label: 'Call' },
  { value: NextAction.EMAIL, label: 'Email' },
  { value: NextAction.SCHEDULE_ESTIMATE, label: 'Schedule Estimate' },
  { value: NextAction.SEND_QUOTE, label: 'Send Quote' },
  { value: NextAction.FOLLOW_UP, label: 'Follow Up' },
  { value: NextAction.OTHER, label: 'Other' },
]

interface FormErrors {
  first_name?: string
  last_name?: string
  contact?: string
  email?: string
  source_channel?: string
  assigned_to?: string
}

// Email validation regex matching Pydantic's EmailStr
const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/
  return emailRegex.test(email)
}

export function LeadFormPage() {
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const { addToast } = useUIStore()
  const createMutation = useCreateLead()
  const { data: users } = useUsers()

  const [formData, setFormData] = useState<Partial<LeadCreate>>({
    assigned_to: user?.id,
    source_channel: undefined,
  })
  const [errors, setErrors] = useState<FormErrors>({})
  const [duplicateData, setDuplicateData] = useState<DuplicateCheckResponse | null>(null)
  const [showDuplicateDialog, setShowDuplicateDialog] = useState(false)
  const [pendingFormData, setPendingFormData] = useState<LeadCreate | null>(null)
  const [isCheckingDuplicate, setIsCheckingDuplicate] = useState(false)

  const handleChange = (
    field: keyof LeadCreate,
    value: string | number | undefined
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
    // Clear error when field is updated
    if (errors[field as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }))
    }
  }

  const handleAddressPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const text = e.clipboardData.getData('text').trim()
    // Matches: "3212 Bennett Dr, Naperville, IL 60564"
    const match = text.match(/^(.+?),\s*(.+?),\s*[A-Z]{2}\s+(\d{5}(?:-\d{4})?)$/)
    if (!match) return
    e.preventDefault()
    handleChange('address', match[1].trim())
    handleChange('city', match[2].trim())
    handleChange('zip', match[3])
  }

  const validate = (): boolean => {
    const newErrors: FormErrors = {}

    if (!formData.first_name?.trim()) {
      newErrors.first_name = 'First name is required'
    }
    if (!formData.last_name?.trim()) {
      newErrors.last_name = 'Last name is required'
    }
    if (!formData.phone?.trim() && !formData.email?.trim()) {
      newErrors.contact = 'Phone or email is required'
    }
    if (formData.email?.trim() && !isValidEmail(formData.email.trim())) {
      newErrors.email = 'Please enter a valid email address'
    }
    if (!formData.source_channel) {
      newErrors.source_channel = 'Source channel is required'
    }
    if (!formData.assigned_to) {
      newErrors.assigned_to = 'Assigned user is required'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validate()) {
      addToast({
        title: 'Validation Error',
        description: 'Please fill in all required fields',
        variant: 'error',
      })
      return
    }

    const data = formData as LeadCreate
    setIsCheckingDuplicate(true)

    try {
      const result = await leadService.checkDuplicate({
        first_name: data.first_name,
        last_name: data.last_name,
        email: data.email,
        phone: data.phone,
      })

      if (result.is_duplicate) {
        setPendingFormData(data)
        setDuplicateData(result)
        setShowDuplicateDialog(true)
      } else {
        createMutation.mutate(data, {
          onSuccess: (created) => navigate(`/leads/${created.id}`),
        })
      }
    } catch {
      // If the check fails, proceed with creation so users aren't blocked
      createMutation.mutate(data, {
        onSuccess: (created) => navigate(`/leads/${created.id}`),
      })
    } finally {
      setIsCheckingDuplicate(false)
    }
  }

  const handleGoToExisting = () => {
    if (duplicateData?.lead) navigate(`/leads/${duplicateData.lead.id}`)
  }

  const handleCreateDuplicate = () => {
    setShowDuplicateDialog(false)
    if (pendingFormData) {
      createMutation.mutate(pendingFormData, {
        onSuccess: (created) => navigate(`/leads/${created.id}`),
      })
    }
  }

  return (
    <div className="mx-auto max-w-3xl min-[1300px]:max-w-none space-y-3">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/leads')}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <h1 className="text-2xl font-bold">Create New Lead</h1>
      </div>

      <form onSubmit={handleSubmit} className="space-y-3">
        {/* Contact Information & Lead Information - side by side on large screens */}
        <div className="grid gap-6 min-[1300px]:grid-cols-2">
        {/* Contact Information */}
        <Card>
          <CardHeader>
            <CardTitle>Contact Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="first_name">
                  First Name <span className="text-error">*</span>
                </Label>
                <Input
                  id="first_name"
                  value={formData.first_name || ''}
                  onChange={(e) => handleChange('first_name', e.target.value)}
                  error={!!errors.first_name}
                  maxLength={100}
                />
                {errors.first_name && (
                  <p className="text-sm text-error">{errors.first_name}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="last_name">
                  Last Name <span className="text-error">*</span>
                </Label>
                <Input
                  id="last_name"
                  value={formData.last_name || ''}
                  onChange={(e) => handleChange('last_name', e.target.value)}
                  error={!!errors.last_name}
                  maxLength={100}
                />
                {errors.last_name && (
                  <p className="text-sm text-error">{errors.last_name}</p>
                )}
              </div>
            </div>

            {errors.contact && (
              <p className="text-sm text-error">{errors.contact}</p>
            )}

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email || ''}
                  onChange={(e) => handleChange('email', e.target.value)}
                  error={!!errors.contact || !!errors.email}
                  maxLength={255}
                />
                {errors.email && (
                  <p className="text-sm text-error">{errors.email}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  value={formData.phone || ''}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  error={!!errors.contact}
                  maxLength={50}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="company_name">Company Name</Label>
              <Input
                id="company_name"
                value={formData.company_name || ''}
                onChange={(e) => handleChange('company_name', e.target.value)}
                maxLength={255}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="address">Address</Label>
              <Input
                id="address"
                value={formData.address || ''}
                onChange={(e) => handleChange('address', e.target.value)}
                onPaste={handleAddressPaste}
                maxLength={500}
              />
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="city">City</Label>
                <Input
                  id="city"
                  value={formData.city || ''}
                  onChange={(e) => handleChange('city', e.target.value)}
                  maxLength={100}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="zip">ZIP Code</Label>
                <Input
                  id="zip"
                  value={formData.zip || ''}
                  onChange={(e) => handleChange('zip', e.target.value)}
                  maxLength={20}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Lead Information */}
        <Card>
          <CardHeader>
            <CardTitle>Lead Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>
                  Source Channel <span className="text-error">*</span>
                </Label>
                <Select
                  value={formData.source_channel || ''}
                  onValueChange={(v) =>
                    handleChange('source_channel', v as SourceChannel)
                  }
                >
                  <SelectTrigger error={!!errors.source_channel}>
                    <SelectValue placeholder="Select source" />
                  </SelectTrigger>
                  <SelectContent>
                    {sourceOptions.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.source_channel && (
                  <p className="text-sm text-error">{errors.source_channel}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="source_campaign">Campaign</Label>
                <Input
                  id="source_campaign"
                  value={formData.source_campaign || ''}
                  onChange={(e) =>
                    handleChange('source_campaign', e.target.value)
                  }
                  maxLength={255}
                />
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="referrer_name">Referrer Name</Label>
                <Input
                  id="referrer_name"
                  value={formData.referrer_name || ''}
                  onChange={(e) => handleChange('referrer_name', e.target.value)}
                  maxLength={255}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="lead_score">Lead Score (0-100)</Label>
                <Input
                  id="lead_score"
                  type="number"
                  min={0}
                  max={100}
                  value={formData.lead_score ?? ''}
                  onChange={(e) =>
                    handleChange(
                      'lead_score',
                      e.target.value ? parseInt(e.target.value, 10) : undefined
                    )
                  }
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Project Size</Label>
              <Select
                value={formData.project_size || ''}
                onValueChange={(v) =>
                  handleChange('project_size', v as ProjectSize)
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select size" />
                </SelectTrigger>
                <SelectContent>
                  {projectSizeOptions.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="pain_points">Pain Points</Label>
              <Textarea
                id="pain_points"
                value={formData.pain_points || ''}
                onChange={(e) => handleChange('pain_points', e.target.value)}
                rows={3}
              />
            </div>
          </CardContent>
        </Card>
        </div>

        {/* Assignment & Actions */}
        <Card>
          <CardHeader>
            <CardTitle>Assignment & Next Steps</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>
                  Assigned To <span className="text-error">*</span>
                </Label>
                <Select
                  value={formData.assigned_to || ''}
                  onValueChange={(v) => handleChange('assigned_to', v)}
                >
                  <SelectTrigger error={!!errors.assigned_to}>
                    <SelectValue placeholder="Select user" />
                  </SelectTrigger>
                  <SelectContent>
                    {users?.map((u) => (
                      <SelectItem key={u.id} value={u.id}>
                        {u.username}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.assigned_to && (
                  <p className="text-sm text-error">{errors.assigned_to}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label>Next Action</Label>
                <Select
                  value={formData.next_action || ''}
                  onValueChange={(v) =>
                    handleChange('next_action', v as NextAction)
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select action" />
                  </SelectTrigger>
                  <SelectContent>
                    {nextActionOptions.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="next_action_date">Next Action Date</Label>
              <Input
                id="next_action_date"
                type="date"
                value={formData.next_action_date || ''}
                onChange={(e) =>
                  handleChange('next_action_date', e.target.value)
                }
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={formData.notes || ''}
                onChange={(e) => handleChange('notes', e.target.value)}
                rows={4}
              />
            </div>
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex justify-end gap-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/leads')}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={createMutation.isPending || isCheckingDuplicate}>
            {isCheckingDuplicate ? 'Checking...' : createMutation.isPending ? 'Creating...' : 'Create Lead'}
          </Button>
        </div>
      </form>

      {duplicateData?.lead && (
        <Dialog open={showDuplicateDialog} onOpenChange={setShowDuplicateDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Duplicate Contact Detected</DialogTitle>
              <DialogDescription>
                This contact has been identified as existing, please input the details in
                the first registration of this contact or accept the creation of a duplicate.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <p className="text-sm font-medium">The following fields match an existing contact:</p>
              <ul className="text-sm space-y-1">
                {duplicateData.matched_fields.includes('name') && (
                  <li>
                    <span className="font-medium">Name: </span>
                    {duplicateData.lead.first_name} {duplicateData.lead.last_name}
                  </li>
                )}
                {duplicateData.matched_fields.includes('email') && (
                  <li>
                    <span className="font-medium">Email: </span>
                    {duplicateData.lead.email}
                  </li>
                )}
                {duplicateData.matched_fields.includes('phone') && (
                  <li>
                    <span className="font-medium">Phone: </span>
                    {duplicateData.lead.phone}
                  </li>
                )}
              </ul>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                className="border-destructive text-destructive hover:bg-destructive/10"
                onClick={handleCreateDuplicate}
                disabled={createMutation.isPending}
              >
                {createMutation.isPending ? 'Creating...' : 'Create a duplicate'}
              </Button>
              <Button onClick={handleGoToExisting}>
                Go to existing contact
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  )
}
